import { useState, useCallback, useEffect } from "react";
import { FiMail, FiCheck, FiXCircle } from "react-icons/fi";
import { cn, showToast } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import Modal from "newui/components/ui/Modal/Modal";
import { useCurrentUser } from "hooks/auth";
import { useRfxData } from "../../data-provider/RfxDataProvider";
import { RfxDetails } from "../../Submission/components/ViewSubmission";
import { CompanyInfo } from "../../Submission/components/AcceptDeclineModal/CompanyInfo";
import { setSwitchFromGuest } from "newui/features/Auth/utils";
import { useEvaluation } from "../hooks/useEvaluation";
import { acceptGuestPanelInvitation } from "../api/panel-scoring-api";
import { DeclineDrawer } from "../components/steps/InitialEvaluationStep/panel/member/DeclineDrawer";
import { DisclaimerModal } from "../components/DisclaimerModal";
import { EVALUATION_STEP_NUMBERS } from "../constants/evaluation.constants";
import type { GuestCompanyData } from "../../Submission/types/guest-company-data.type";

export const GuestEvaluationPanelPage = ({ path: _path }) => {
	const { hasRfxLoaded, rfxData, rfxId } = useRfxData();

	const { isLoading: isEvalLoading, evaluationState, currentUserContext, fetchEvaluationState } = useEvaluation({
		rfxId,
	});

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const [showCompanyModal, setShowCompanyModal] = useState(false);
	const [showDeclineDrawer, setShowDeclineDrawer] = useState(false);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const [isAccepting, setIsAccepting] = useState(false);

	const evalId = evaluationState?.id;
	const memberId = currentUserContext?.panelMemberId;
	const memberStatus = currentUserContext?.panelMemberStatus;
	const currentStep = evaluationState?.currentStep ?? 0;
	const disclaimerRequired = currentUserContext?.disclaimerRequired ?? false;

	const effectiveStatus = memberStatus;
	const isAccepted = effectiveStatus === "accepted" || effectiveStatus === "in_progress" || effectiveStatus === "submitted";
	const isDeclined = effectiveStatus === "declined";
	const isInvited = effectiveStatus === "invited" || !effectiveStatus;
	const canReAccept = currentStep < EVALUATION_STEP_NUMBERS.CLARIFICATIONS;

	/**
	 * PRO-637: accepted, but still a guest with no company — onboarding stalled
	 * part-way. This is reachable, not theoretical: submitting the disclaimer is
	 * what marks the member `accepted` server-side
	 * (PanelDisclaimerService::submitDisclaimer), so between that call and the
	 * company-creation call the member is already accepted. A refresh, a network
	 * failure or a dismissed modal in that window used to land them on the plain
	 * "You have accepted" banner with no route to finish — accepted, company-less
	 * and stuck. Offer the company step again; the accept endpoint is idempotent
	 * on an already-accepted member, so re-submitting is safe.
	 */
	const { isGuest } = useCurrentUser() ?? { isGuest: false };
	const needsCompanyOnboarding = isAccepted && isGuest;

	// I2 ruling (PRO-637 wave-2 integration): cross-company/guest panellists must
	// clear the disclaimer step before proceeding to company creation — same
	// gate InvitationBanner.tsx applies for company-user panel members.
	const handleAcceptClick = useCallback(() => {
		if (disclaimerRequired) {
			setShowDisclaimer(true);
			return;
		}
		setShowCompanyModal(true);
	}, [disclaimerRequired]);

	const handleDeclineClick = useCallback(() => {
		setShowDeclineDrawer(true);
	}, []);

	const handleReAccept = useCallback(() => {
		if (disclaimerRequired) {
			setShowDisclaimer(true);
			return;
		}
		setShowCompanyModal(true);
	}, [disclaimerRequired]);

	// Guest onboarding always needs a company account regardless of conflict
	// status, so both outcomes proceed to company creation — a declared
	// conflict is flagged for the RFx Lead to review post-acceptance, it does
	// not block onboarding itself (unlike the company-user InvitationBanner
	// path, which has no separate onboarding step to fall through to).
	const handleDisclaimerComplete = useCallback(() => {
		setShowDisclaimer(false);
		setShowCompanyModal(true);
	}, []);

	const handleDisclaimerCancel = useCallback(() => {
		setShowDisclaimer(false);
	}, []);

	const handleCompanySubmit = useCallback(async (companyData: GuestCompanyData) => {
		if (!rfxId || !evalId || !memberId) return;

		setIsAccepting(true);
		try {
			const response = await acceptGuestPanelInvitation(rfxId, evalId, memberId, companyData as unknown as Record<string, unknown>);
			showToast("Your invitation has been accepted and your company account created.", "Invitation Accepted", true);
			setShowCompanyModal(false);

			if (response.company && response.user) {
				// Land them ON the evaluation they were invited to, not the RFx list.
				// They arrived here from a panel-invitation email for one specific
				// evaluation; dropping them on the list made them hunt for it again
				// immediately after accepting.
				//
				// Built by hand rather than via useNavigationPaths(): the hook derives
				// its base path from the CURRENT company, which is still the guest
				// session at this point — the switch to `response.company` has not
				// been applied yet, so the hook would produce the wrong account id.
				// Same reason the previous `/account/{id}/rfx` literal was hand-built.
				const newCompanyId = (response.company as any).id;
				setSwitchFromGuest(
					response.company as any,
					response.user as any,
					`/account/${newCompanyId}/rfx/${rfxId}/evaluation/${evalId}`
				);
			} else {
				fetchEvaluationState();
			}
		} catch {
			showToast("Failed to accept invitation. Please try again.", "Error", false);
		} finally {
			setIsAccepting(false);
		}
	}, [rfxId, evalId, memberId, fetchEvaluationState]);

	const handleDeclined = useCallback(() => {
		setShowDeclineDrawer(false);
		showToast("Your decline has been sent to the RFx Lead.", "Invitation Declined", true);
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const isLoading = !hasRfxLoaded || isEvalLoading;

	if (isLoading) {
		return (
			<div className="space-y-4 p-6">
				<SkeletonBase startColor="lightgray" width="100%" height="120px" className="!rounded-lg" />
				<SkeletonBase startColor="lightgray" width="100%" height="200px" className="!rounded-lg" />
			</div>
		);
	}

	return (
		<div className="space-y-6 p-6" data-testid="guest-evaluation-panel-page">
			{isInvited && (
				<div
					className="rounded-lg border border-[#0031DD]/20 bg-[#e8eeff] px-6 py-5"
					data-testid="guest-invitation-banner"
				>
					<div className="flex items-start gap-3">
						<FiMail size={24} className="mt-0.5 shrink-0 text-[#0031DD]" aria-hidden="true" />
						<div className="flex-1">
							<p className="text-sm font-medium text-gray-900">
								You have been invited to participate as a Panel member to Evaluate the RFx below.
							</p>
							<p className="mt-1 text-sm text-gray-600">
								To accept and start reviewing the submissions, please accept the invitation and create your company account.
							</p>

							<div className="mt-4 flex items-center gap-3">
								<button
									type="button"
									onClick={handleAcceptClick}
									className={cn(
										"inline-flex items-center gap-1.5 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors",
										"bg-[#0031DD] text-white hover:bg-[#0028b8] active:bg-[#002099]",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2"
									)}
									data-testid="guest-accept-btn"
								>
									<FiCheck size={16} aria-hidden="true" />
									Accept Invite
								</button>
								<button
									type="button"
									onClick={handleDeclineClick}
									className={cn(
										"inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors",
										"hover:bg-gray-50 active:bg-gray-100",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2"
									)}
									data-testid="guest-decline-btn"
								>
									Decline Invite
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{needsCompanyOnboarding && (
				<div
					className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
					data-testid="guest-company-pending-banner"
				>
					<FiCheck size={18} className="shrink-0 text-amber-600" aria-hidden="true" />
					<p className="text-sm text-amber-800">
						You have accepted this invitation. Create your company account to start evaluating.
					</p>
					<button
						type="button"
						onClick={() => setShowCompanyModal(true)}
						className="ml-auto rounded-md bg-[#0031DD] px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-[#0028b8]"
						data-testid="guest-create-company-btn"
					>
						Create Company Account
					</button>
				</div>
			)}

			{isAccepted && !needsCompanyOnboarding && (
				<div
					className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3"
					data-testid="guest-accepted-banner"
				>
					<FiCheck size={18} className="shrink-0 text-green-600" aria-hidden="true" />
					<p className="text-sm text-green-700">
						You have accepted to score this Evaluation.
					</p>
					<button
						type="button"
						onClick={handleDeclineClick}
						className="ml-auto rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-800 transition-colors"
						data-testid="guest-withdraw-btn"
					>
						I can no longer participate
					</button>
				</div>
			)}

			{isDeclined && (
				<div
					className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
					data-testid="guest-declined-banner"
				>
					<FiXCircle size={18} className="shrink-0 text-red-600" aria-hidden="true" />
					<p className="text-sm text-red-700">
						You have declined to participate. You cannot score this Evaluation.
					</p>
					{canReAccept && (
						<button
							type="button"
							onClick={handleReAccept}
							className="ml-auto rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-800 transition-colors"
							data-testid="guest-reaccept-btn"
						>
							I want to participate
						</button>
					)}
				</div>
			)}

			<RfxDetails
				rfxData={rfxData}
				hidePreviewLink={true}
				isLoading={!hasRfxLoaded}
			/>

			{showCompanyModal && (
				<Modal
					isOpen={showCompanyModal}
					onClose={() => setShowCompanyModal(false)}
					title="Accept Invitation & Create Company Account"
					size="6xl"
					iconType="info"
					modalType="standard"
					closeOnEsc={!isAccepting}
					closeOnOverlayClick={false}
				>
					<div className="space-y-4" data-testid="guest-company-modal">
						<div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3">
							<p className="text-sm text-blue-800">
								Create your company account to start evaluating this RFx.
								Build your profile, showcase your projects, and participate in the evaluation panel.
							</p>
						</div>
						<CompanyInfo
							onSubmit={handleCompanySubmit}
							onCancel={() => setShowCompanyModal(false)}
							isLoading={isAccepting}
						/>
					</div>
				</Modal>
			)}

			{rfxId && evalId && memberId && (
				<DeclineDrawer
					isOpen={showDeclineDrawer}
					onClose={() => setShowDeclineDrawer(false)}
					rfxId={rfxId}
					evalId={evalId}
					memberId={memberId}
					rfxLeadName={rfxData?.rfxLead?.fullName}
					rfxLeadEmail={rfxData?.rfxLead?.email}
					rfxTitle={rfxData?.name}
					onDeclined={handleDeclined}
				/>
			)}

			{rfxId && evalId && memberId && (
				<DisclaimerModal
					isOpen={showDisclaimer}
					onClose={handleDisclaimerCancel}
					onSubmitSuccess={handleDisclaimerComplete}
					rfxId={rfxId}
					evalId={evalId}
					memberId={memberId}
				/>
			)}
		</div>
	);
};
